import {
  TIME_UNIT_OPTIONS,
  fieldCannotBeBlankErrorMessage,
} from '@ben/columbia/components/shared/constants';
import type { BenLanguageBackendCode } from '@ben/common/languages';
import Checkbox from '@ben/orion/checkbox';
import Select from '@ben/orion/deprecated-select';
import Input, { InputVariant } from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import { StartsOnEnum } from '@ben/shared/types/modular-benefit';
import { decodeHtmlEntities } from '@ben/shared/utils/decode-html-entities';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import TranslatableField from '../general-information/translatable-field';
import { AutoEnrolmentEligibilityQuery } from './auto-enrolment-eligibility-query';
import {
  DEFAULT_POSTPONEMENT_BANNER_BODY_EN,
  DEFAULT_POSTPONEMENT_BANNER_HEADER_EN,
} from './constants';

const STARTS_ON_OPTIONS = [
  { label: 'Eligibility start date', key: StartsOnEnum.ELIGIBILITY_START },
  { label: 'Employment start date', key: StartsOnEnum.EMPLOYMENT_START },
  { label: 'Probation end date', key: StartsOnEnum.PROBATION_END },
];

export const PostponementConfig = () => {
  const { control, setValue } = useFormContext<IModularBenefitDetail>();
  const bannerHeaderEn = useWatch({
    name: 'enrollmentConfig.bannerHeaderEn',
    exact: true,
  });
  const bannerBodyEn = useWatch({
    name: 'enrollmentConfig.bannerBodyEn',
    exact: true,
  });

  useEffect(() => {
    if (!bannerHeaderEn) {
      setValue(
        'enrollmentConfig.bannerHeaderEn',
        DEFAULT_POSTPONEMENT_BANNER_HEADER_EN
      );
    }
    if (!bannerBodyEn) {
      setValue(
        'enrollmentConfig.bannerBodyEn',
        DEFAULT_POSTPONEMENT_BANNER_BODY_EN
      );
    }
  }, [bannerHeaderEn, bannerBodyEn, setValue]);

  return (
    <PostponementContainer>
      <Typography variant={TypographyVariant.bodyLg2}>
        Postponement window starts on
      </Typography>
      <ContentRow>
        <Controller
          name="enrollmentConfig.startsOn"
          control={control}
          render={({ field: { value, ref, ...field } }) => (
            <Select
              ref={ref}
              key={value}
              defaultValue={value}
              {...field}
              name="Postponement window starts on"
              options={STARTS_ON_OPTIONS}
            />
          )}
        />
        <Controller
          name="enrollmentConfig.enrollAfter"
          control={control}
          render={({ field }) => (
            <Input {...field} type="number" name="Enroll after" width="80px" />
          )}
        />
        <Controller
          name="enrollmentConfig.timeUnit"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              name="Time unit"
              options={TIME_UNIT_OPTIONS}
              width="150px"
            />
          )}
        />
      </ContentRow>
      <Controller
        name="enrollmentConfig.includeUkPensionLink"
        control={control}
        defaultValue={false}
        render={({ field: { value, ...field } }) => (
          <Checkbox
            {...field}
            value={value || false}
            label="Include link to the UK regulation in the employee email"
            hintText="If checked, a link to the GOV.UK regulation on auto-enrolment and workplace pensions will be included in the email."
          />
        )}
      />
      <Controller
        name="enrollmentConfig.cancellable"
        control={control}
        defaultValue={false}
        render={({ field: { value, ...field } }) => (
          <Checkbox
            {...field}
            value={value || false}
            label="Allow employee to cancel auto-enrolment"
          />
        )}
      />
      <AutoEnrolmentEligibilityQuery />
      <TranslatableField
        inputLabel="Banner header"
        hintText="Use this to inform the user about the auto-enrollment process and clarify whether they have the option to opt in earlier."
      >
        {(language: BenLanguageBackendCode) => (
          <Controller
            name={`enrollmentConfig.bannerHeader${language}`}
            control={control}
            rules={{
              required: language === 'En',
            }}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  value={value ? decodeHtmlEntities(value) : undefined}
                  width="100%"
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  showLabel={false}
                  maxLength={70}
                />
              );
            }}
          />
        )}
      </TranslatableField>
      <TranslatableField inputLabel="Banner body">
        {(language: BenLanguageBackendCode) => (
          <Controller
            name={`enrollmentConfig.bannerBody${language}`}
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;

              return (
                <Input
                  {...rest}
                  variant={InputVariant.TextArea}
                  value={value ? decodeHtmlEntities(value) : undefined}
                  width="100%"
                  height="150px"
                  showLabel={false}
                />
              );
            }}
          />
        )}
      </TranslatableField>
    </PostponementContainer>
  );
};

const PostponementContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `};
`;

export const ContentRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `};
`;
